import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Color } from "three";
import { fetchSensorPlacementForZones } from "../../api/sensorPlacementApi";
import { useLazyKeyedResource } from "../../hooks/useLazyKeyedResource";
import {
  showFirstSensor,
  updateRangeSensorPlacement,
  updateSensorPlacementPosition,
} from "../../redux/infoWindRisk";
import { setSensorPlacementIsLoading } from "../../redux/shadingSystem";
import { useTheme } from "../../styles/themes/themeContext";
import { useColorBar, useSensorPlacementColorBar } from "../CustomHooks";
import { resolveOverallScore } from "../utility/sensorPlacement";
import { reduxLocalStorage } from "../utility/storage";
import {
  filterObjectsByColorbarRange,
  generateColor,
  getRangeModeDisplayColorFn,
  sensorPlacementScale,
} from "../utils";
import { SphereInstancedMesh } from "./InstancedMesh";

const Zone = (props) => {
  var dispatch = useDispatch();
  function generateMinMax(valueList) {
    const minmaxList = [];
    for (let i = 0; i < valueList.length; i++) {
      minmaxList.push(resolveOverallScore(valueList[i]));
    }

    const minVal = Math.min(...minmaxList);
    const maxVal = Math.max(...minmaxList);
    return { minVal, maxVal };
  }

  var windRiskSelector = useSelector((state) => state.shadingSystem.value);

  useEffect(() => {
    if (props.colorMode === "uniform" || !props.data?.length) return;
    var sensorPlacementRangeList = generateMinMax(props.data);
    dispatch(
      updateRangeSensorPlacement([
        sensorPlacementRangeList.minVal,
        sensorPlacementRangeList.maxVal,
      ]),
    );
  }, [props]);

  const theme = useTheme();
  const { max, min, rangeMode } = useColorBar("Sensor Placement", "Zone", 0, 1);
  const {
    sensorPlacementData: selectedSensorData,
    firstSensorPlacementIsShowing,
  } = useSelector((store) => store.infoWindRisk);

  const isUniform = props.colorMode === "uniform";

  const displaySensors = useMemo(() => {
    if (isUniform || !props.data?.length) return props.data || [];
    return filterObjectsByColorbarRange(
      props.data,
      (sensor) => resolveOverallScore(sensor),
      min,
      max,
      rangeMode,
    );
  }, [isUniform, props.data, min, max, rangeMode]);

  const setColor = useCallback(
    (sensor) => {
      if (isUniform) {
        return new Color(props.uniformColor || theme.primary);
      }
      const { color } = getRangeModeDisplayColorFn(
        resolveOverallScore(sensor),
        {
          min,
          max,
          rangeMode,
          getScaledColor: (value) =>
            generateColor(
              value,
              Number(min),
              Number(max),
              sensorPlacementScale,
            ),
        },
      );
      return new Color(color);
    },
    [isUniform, props.uniformColor, theme.primary, min, max, rangeMode],
  );

  const onClick = (config) => {
    const sensor = displaySensors[config.instanceId];
    if (!sensor) return;
    dispatch(
      updateSensorPlacementPosition([
        config.pageX,
        config.pageY,
        sensor,
        sensor.height ?? sensor.coordinates?.local?.z,
        displaySensors.length,
      ]),
    );
    dispatch(showFirstSensor());
    config.stopPropagation();
  };

  const getObjectPosition = useCallback((sensor) => {
    return sensor?.position;
  }, []);

  const isVisible =
    props.visible != null
      ? props.visible
      : windRiskSelector.checkStatus[props.id];

  const radius = props.radius ?? 0.4;
  const highlightRadius = props.highlightRadius ?? radius * 2.25;

  if (displaySensors?.length)
    return (
      <SphereInstancedMesh
        objects={displaySensors}
        setColor={setColor}
        groupProps={{
          onClick,
          visible: isVisible,
        }}
        getObjectPosition={getObjectPosition}
        radius={radius}
        updateDependencyArray={[min, max, rangeMode, isUniform]}
        highlightProps={{
          highlightedObject: [selectedSensorData],
          showHighlight: firstSensorPlacementIsShowing,
          radius: highlightRadius,
          getObjectPosition,
        }}
      />
    );
};

export { Zone as SensorPlacementZone };

const SensorPlacement = () => {
  const projectId = reduxLocalStorage.getItem("project_id");
  const dispatch = useDispatch();
  const sensorPlacementZoneConfig =
    useSelector((state) => state.sceneVar.sensorPlacementZoneConfig) ?? [];
  const windRiskSelector = useSelector((state) => state.shadingSystem.value);
  const { sensorCandidatesVisible } = useSensorPlacementColorBar();

  const activeZones = useMemo(
    () =>
      sensorCandidatesVisible
        ? sensorPlacementZoneConfig.filter(
            (_zone, i) => windRiskSelector.checkStatus[i + 3],
          )
        : [],
    [
      sensorCandidatesVisible,
      sensorPlacementZoneConfig,
      windRiskSelector.checkStatus,
    ],
  );

  const fetcher = useCallback(
    (zones) => fetchSensorPlacementForZones(projectId, zones),
    [projectId],
  );

  const onLoadingChange = useCallback(
    (loading) => dispatch(setSensorPlacementIsLoading(loading)),
    [dispatch],
  );

  const { dataByKey } = useLazyKeyedResource({
    keys: activeZones,
    fetcher,
    enabled: Boolean(projectId) && activeZones.length > 0,
    onLoadingChange,
  });

  return sensorPlacementZoneConfig.map((zone, i) => {
    const id = i + 3;
    const data = dataByKey[zone];
    if (!windRiskSelector.checkStatus[id] || !data?.length) return null;
    return <Zone key={zone} id={id} data={data} />;
  });
};

export default SensorPlacement;
